// Small presentational pieces the administration screens share, so every list,
// form and panel looks like the user-master / user-role design.
//
// Display only — nothing here holds state, fetches, or touches an API payload.
import React from "react";

/* ---------------------------------------------------------------- helpers */

// Two-letter tile text: "Rashid Kunhi" -> "RK".
export const axInitials = (name) => {
  const words = String(name || "")
    .replace(/[^\p{L}\p{N} ]/gu, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "—";
  const first = words[0];
  const second = words[1] ? words[1][0] : first[1] || "";
  return (first[0] + second).toUpperCase();
};

// Stable hue (0-359) from any key, so a name always gets the same colour.
export const axHue = (key) => {
  const s = String(key || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return s ? h % 360 : 228;
};

/* ------------------------------------------------------------------ icons */

export const AxSearchIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <circle cx="7.2" cy="7.2" r="4.6" />
    <path d="M10.6 10.6 14 14" />
  </svg>
);
export const AxClearIcon = () => (
  <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M3 3l6 6M9 3l-6 6" />
  </svg>
);
export const AxPlusIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
    <path d="M8 2.6v10.8M2.6 8h10.8" />
  </svg>
);
export const AxCaret = ({ className = "axa-caret" }) => (
  <svg className={className} viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 5l3 3 3-3" />
  </svg>
);
export const AxSendIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M14.4 1.8 7.2 9M14.4 1.8l-4.6 12.4-2.6-5.2-5.2-2.6z" />
  </svg>
);
export const AxRefreshIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M2.6 8a5.4 5.4 0 1 0 1.6-3.8" />
    <path d="M2.2 2.6v3.2h3.2" />
  </svg>
);
export const AxPdfIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M4 1.8h5l3 3v9.4H4z" />
    <path d="M9 1.8v3h3" />
  </svg>
);
export const AxExcelIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="2.2" y="2.2" width="11.6" height="11.6" rx="1.5" />
    <path d="M5.6 5.6l4.8 4.8M10.4 5.6l-4.8 4.8" />
  </svg>
);
export const AxPrintIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M4.4 6V2.2h7.2V6" />
    <rect x="2" y="6" width="12" height="5" rx="1.2" />
    <path d="M4.4 9.6h7.2v4.2H4.4z" />
  </svg>
);
export const AxTableIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
    <path d="M2 6.2h12M6.4 6.2v7.3" />
  </svg>
);
export const AxEditIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M11.2 2.4 13.6 4.8 5.6 12.8 2.4 13.6l.8-3.2z" />
  </svg>
);
export const AxTrashIcon = () => (
  <svg viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M2.5 4h9M5.6 4V2.6h2.8V4M4 4l.5 7.4h5L10 4" />
  </svg>
);
export const AxLockIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="3" y="7" width="10" height="7" rx="1.4" />
    <path d="M5.4 7V5a2.6 2.6 0 0 1 5.2 0v2" />
  </svg>
);
export const AxBranchIcon = () => (
  <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M1.6 4.6 6 1.6l4.4 3v5.8H1.6z" />
  </svg>
);
export const AxPersonIcon = () => (
  <svg viewBox="0 0 48 48" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="24" cy="15" r="7" />
    <path d="M10 40c0-7.7 6.3-14 14-14s14 6.3 14 14" />
  </svg>
);
export const AxInfoIcon = () => (
  <svg viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="7" cy="7" r="5.6" />
    <path d="M7 6.2v3.6M7 4.3v.9" />
  </svg>
);
export const AxWarnIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M8 2.2 14.4 13.4H1.6z" />
    <path d="M8 6.4v3M8 11.2v.8" />
  </svg>
);
export const AxDotIcon = () => (
  <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="6" cy="6" r="2.4" />
  </svg>
);

/* ------------------------------------------------------------------ parts */

// The initials tile used in a row, a chip and the detail header.
export const AxAvatar = ({ name, className = "" }) => (
  <span className={"axa-av " + className} style={{ "--h": axHue(name) }}>
    {axInitials(name)}
  </span>
);

// New / Unsaved / Saved badge in the detail header.
export const AxPill = ({ state }) => (
  <span className={"axa-pill " + state}>
    {state === "new" ? "New" : state === "dirty" ? "Unsaved" : "Saved"}
  </span>
);

// Uppercase rule that opens a block of fields.
export const AxSectionHead = ({ children }) => (
  <div className="axa-secthead">{children}</div>
);

// A labelled input. `render` lets a caller drop in its own control (a
// react-select, a password row) while keeping the label and hint.
export const AxField = ({
  label,
  required,
  hint,
  bad,
  span,
  full,
  className = "",
  todo,
  children,
}) => (
  <div
    className={
      "axa-f" +
      (full ? " full" : "") +
      (span === 2 ? " span2" : "") +
      (todo ? " todo" : "") +
      (className ? " " + className : "")
    }
  >
    {label ? (
      <label>
        {label}
        {required ? <span className="req">*</span> : null}
        {todo ? <AxTodoTag /> : null}
      </label>
    ) : null}
    {children}
    {hint ? <span className={"hint" + (bad ? " bad" : "")}>{hint}</span> : null}
  </div>
);

// A <select> wearing the design's caret.
export const AxSelectField = ({
  label,
  required,
  hint,
  bad,
  todo,
  span,
  full,
  children,
  ...rest
}) => (
  <AxField
    label={label}
    required={required}
    hint={hint}
    bad={bad}
    todo={todo}
    span={span}
    full={full}
    className="sel"
  >
    <select {...rest}>{children}</select>
    <AxCaret />
  </AxField>
);

// Toggle with a line of explanation underneath.
export const AxSwitchRow = ({ label, note, todo, ...rest }) => (
  <div className={"axa-f axa-switchrow" + (todo ? " todo" : "")}>
    <label className="axa-switch">
      <input type="checkbox" {...rest} />
      <span className="track" />
      <span>
        {label}
        {todo ? <AxTodoTag /> : null}
      </span>
    </label>
    {note ? <span className="d">{note}</span> : null}
  </div>
);

// Marks a control the server cannot store yet (see docs/BACKEND-COMPLETE.md (Part 3)).
export const AxTodoTag = ({ children = "needs backend" }) => (
  <span className="axa-todo" title="This control has no field on the server yet — it is shown for the layout and saves nothing.">
    {children}
  </span>
);

export const AxTodoNote = ({ children }) => (
  <div className="axa-todonote">
    <AxInfoIcon />
    <span>{children}</span>
  </div>
);

// A read-only number / date tile.
export const AxFact = ({ k, v, s }) => (
  <div className="axa-fact">
    <div className="k">{k}</div>
    <div className="v">
      {v}
      {s ? <small>{s}</small> : null}
    </div>
  </div>
);

// One line of a timeline.
export const AxTimelineRow = ({ tone = "", title, sub, when }) => (
  <div className="axa-tl">
    <span className={"ic " + tone}>
      <AxDotIcon />
    </span>
    <span className="tx">
      <b>{title}</b>
      {sub ? <i>{sub}</i> : null}
    </span>
    <span className="when">{when}</span>
  </div>
);

// "Built-in account" style notice at the top of a pane.
export const AxLockBanner = ({ title, children }) => (
  <div className="axa-lockbanner">
    <AxLockIcon />
    <span>
      <b>{title}</b>
      {children}
    </span>
  </div>
);

// The tab strip above a detail pane.
export const AxTabs = ({ tabs, active, onChange }) => (
  <nav className="axa-tabs" role="tablist">
    {tabs.map((t) => (
      <button
        key={t.key}
        type="button"
        role="tab"
        className="axa-tab"
        aria-selected={active === t.key}
        onClick={() => onChange(t.key)}
      >
        {t.label}
        {t.count != null ? <span className="n">{t.count}</span> : null}
        {t.warn ? <span className="warndot" /> : null}
        {t.changed ? <span className="chg" /> : null}
      </button>
    ))}
  </nav>
);

// The detail panel's button row.
export const AxDetailFoot = ({ message, messageIsError, children }) => (
  <div className="axa-dfoot">
    <span className={"msg" + (messageIsError ? " err" : "")}>{message || ""}</span>
    <span className="sp" />
    {children}
  </div>
);

// Nothing picked yet.
export const AxEmptyDetail = ({ title, children, icon }) => (
  <div className="axa-dempty">
    <div className="in">
      {icon || <AxPersonIcon />}
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  </div>
);
